import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactCodeInput from "react-code-input";
import { toast } from "react-toastify";
import Iconify from "src/components/Iconify";
import axiosInstance from "src/utils/axios";

interface LoginCodeDialogProps {
  id: number;
  cpf: string;
  email: string;
  phoneNumber: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
}

const ChannelComponent = ({
  title,
  subtitle,
  icon,
  onClick,
  isLoading = false,
}: {
  title: string;
  subtitle: string;
  icon: string;
  onClick: () => void;
  isLoading?: boolean;
}) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      boxShadow="0 5px 10px rgba(0, 0, 0, 0.1)"
      borderRadius={2}
      padding={2}
      sx={{
        backgroundColor: "white",
        cursor: "pointer",
        transition: "box-shadow 0.3s",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box display="flex" flexDirection="row" alignItems="center">
        <Box
          bgcolor="#e3fcff"
          borderRadius={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={52}
          height={52}
        >
          <Iconify icon={icon} width={24} height={24} color={theme.palette.secondary.main} />
        </Box>
        <Box display="flex" flexDirection="column" flexGrow={1} marginLeft={1}>
          <Typography variant="subtitle1">{title}</Typography>
          <Typography variant="body2" color={theme.palette.grey[500]}>
            {subtitle}
          </Typography>
        </Box>
        {isLoading && <CircularProgress size={24} />}
      </Box>
    </Box>
  );
};

const LoginCodeDialog: React.FC<LoginCodeDialogProps> = ({
  id,
  cpf,
  email,
  phoneNumber,
  open,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [channel, setChannel] = useState<"sms" | "email">("sms");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [code, setCode] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const dialogStyle = { "& .MuiDialog-paper": { backgroundColor: theme.palette.grey[50] } };

  const handleClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  const maskPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/(\d{2})(\d{5})(\d{4})/, "(**) *****-$3");
  };

  const maskEmail = (email: string) => {
    const emailParts = email.split("@");
    const username = emailParts[0];

    return `${username.slice(0, 2)}***${username.slice(-1)}@**.***`;
  };

  function callCRC() {
    window.open("https://wa.me/558440002002?text=Olá,+preciso+de+ajuda+com+acesso+ao+portal.");
  }

  async function onRequestCode(channel: "sms" | "email") {
    if (isLoading) return;

    setChannel(channel);
    setIsLoading(true);

    try {
      if (channel === "email") {
        await axiosInstance.post(`/users/confirmation/mail`, {
          cpf,
          email,
        });
      } else if (channel === "sms") {
        await axiosInstance.post(`/users/confirmation/${phoneNumber}?type=sms`, {
          id,
        });
      }

      setIsCodeSent(true);
    } catch (err) {
      toast.error(
        err.message || "Não foi possível enviar o código. Por favor, tente novamente mais tarde.",
        {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        }
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(code: string) {
    try {
      setSubmitError(null);
      setIsSubmitting(true);
      await onSubmit(code);
    } catch (err) {
      console.error(err);
      const errorMessage =
        (err as any)?.response?.data?.message ||
        (err as Error)?.message ||
        "Não foi possível validar o código. Por favor, tente novamente.";

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (!open) {
      setSubmitError(null);
      setCode("");
      setIsCodeSent(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isCodeSent) {
      setResendCountdown(60);
      setSubmitError(null);
      return;
    }

    setResendCountdown(60);

    const intervalId = window.setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isCodeSent]);

  const BackButton = () => {
    const buttonIcon = isCodeSent ? "mdi:arrow-left" : "mdi:close";
    const handleButtonClick = isCodeSent ? () => setIsCodeSent(false) : onClose;

    return (
      <Button
        variant="text"
        onClick={handleButtonClick}
        sx={{
          position: "absolute",
          left: 12,
          top: 12,
          minWidth: 0,
          color: theme.palette.primary.main,
          fontWeight: "bold",
          textTransform: "none",
          fontSize: "15px",
          borderRadius: "50%",
          width: "36px",
          height: "36px",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": {
            backgroundColor: "#e0e0e0",
          },
        }}
      >
        <Iconify icon={buttonIcon} width={18} height={18} />
      </Button>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={dialogStyle}>
      <DialogTitle align="center">
        <Box display="flex" flexDirection="column" alignItems="center">
          <BackButton />
          <Iconify
            icon="mdi:shield-user-outline"
            width={64}
            height={64}
            mb={1}
            color={theme.palette.secondary.main}
          />
          <Typography
            color={theme.palette.primary.main}
            fontFamily="Open Sans"
            fontSize="24px"
            marginRight="8px"
          >
            Proteção da conta
          </Typography>
          <Typography variant="subtitle2" color={theme.palette.grey[500]} marginTop="10px">
            Para continuar com o acesso, confirme sua identidade escolhendo o método de recebimento
            do código.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ marginTop: "15px" }}>
        {!isCodeSent ? (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" marginTop={2}>
              <Iconify icon="mdi:warning" width={32} height={32} marginRight={2} />
              <Typography variant="body2" textAlign="left">
                Esta etapa é para melhorar a segurança da sua conta e garantir que apenas você tenha
                acesso.
              </Typography>
            </Box>
            <List>
              <ChannelComponent
                key="sms"
                title="Receber por SMS"
                subtitle={maskPhoneNumber(phoneNumber)}
                icon="mdi:message-outline"
                onClick={() => onRequestCode("sms")}
                isLoading={isLoading && channel === "sms"}
              />
              <Box marginTop={2}>
                <ChannelComponent
                  key="email"
                  title="Receber por E-mail"
                  subtitle={maskEmail(email)}
                  icon="mdi:email-outline"
                  onClick={() => onRequestCode("email")}
                  isLoading={isLoading && channel === "email"}
                />
              </Box>
            </List>
            <Typography
              variant="body2"
              color={theme.palette.grey[500]}
              textAlign="center"
              marginTop={4}
              marginBottom={1}
              fontWeight="bold"
            >
              Está com dificuldade de acesso? Entre em contato conosco, estamos prontos para atender
              você!
            </Typography>
            <Button fullWidth size="large" variant="text" onClick={callCRC}>
              <Iconify icon="mdi:whatsapp" width={24} height={24} mr={1} />
              Fale conosco pelo WhatsApp
            </Button>
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="body2" textAlign="center">
              Digite o código de 6 dígitos que você recebeu.
            </Typography>
            <ReactCodeInput
              type="number"
              fields={6}
              name={"code"}
              inputMode={"numeric"}
              onChange={(value: string) => {
                setCode(value);
                if (submitError) setSubmitError(null);
              }}
            />
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              onClick={() => handleSubmit(code)}
            >
              Confirmar código
            </LoadingButton>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            {resendCountdown > 0 ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginTop={4}
                color={theme.palette.grey[500]}
              >
                <Iconify icon="mdi:clock-outline" width={20} height={20} mr={1} />
                <Typography variant="body2" textAlign="center" fontWeight="bold">
                  Reenviar disponível em {Math.floor(resendCountdown / 60)}:
                  {String(resendCountdown % 60).padStart(2, "0")}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  color={theme.palette.grey[500]}
                  textAlign="center"
                  marginTop={4}
                  fontWeight="bold"
                >
                  Não recebeu o código?
                </Typography>
                <Button variant="text" onClick={() => setIsCodeSent(false)}>
                  <Iconify icon="mdi:refresh" width={24} height={24} mr={1} />
                  Clique aqui para reenviar.
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginCodeDialog;
